/*
 * authStorage — surgical localStorage clearing for auth events.
 *
 * Replaces the previous pattern where Login.tsx / Signup.tsx /
 * OAuthCallback.tsx all called `localStorage.clear()` on every
 * auth-flow entry, which blasted domain data like
 * `orbit:turns:v2:<clientId>` (chat history) and
 * `orbit:active-turn-idx:v1:<clientId>` (pane position).
 *
 * The bug surfaced as: user signs in with Gmail → fills 20+ Orbit
 * intake fields → reloads → chat history vanishes, the opener
 * re-pushes, "01/01" shows despite 23/49 captured on the server.
 *
 * Auth keys are an explicit allowlist below. Anything not on the
 * list survives — domain data is the default. New auth keys MUST
 * be added here; new domain keys need no change.
 *
 * logout() in AuthContext keeps the full `localStorage.clear()`
 * because the user explicitly chose to leave — full wipe is the
 * expected behavior there.
 */


/** Auth-session keys that should be cleared on login/signup/oauth-
 *  callback entry to drop any prior session's lingering tokens. */
const AUTH_KEYS: readonly string[] = [
  'auth_token',
  'refresh_token',
  'user_role',
  'user_id',
  'user_email',
  'user_name',
  'client_id',
  'client_name',
  'last_client_id',
  'provider_id',
  'is_main_provider',
  // Impersonation pair — drop with any auth event so a stale
  // impersonation session can't bleed across logins.
  'impersonation_token',
  'impersonated_user_id',
  'impersonated_client_id',
  'impersonated_user_role',
  'original_token',
  'original_user_id',
  'original_user_role',
];


/**
 * Drop only the auth-session keys from localStorage. Preserves all
 * domain data (orbit:turns:*, orbit:active-turn-idx:*, etc.) so
 * users returning through Gmail / login / signup keep their state.
 */
export function clearAuthState(): void {
  for (const key of AUTH_KEYS) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // private mode / storage disabled — ignore
    }
  }
}
